import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface AiStatus {
  ready: boolean;
  reason: 'not_set_up' | 'off' | 'no_credits' | null;
  canUse: boolean;
  balancePaise: number | null;
  callInsights: boolean;
}

/** What AI can do right now; the app hides or explains AI buttons based on this. */
export const useAiStatus = () => useQuery({ queryKey: ['ai', 'status'], queryFn: () => api.get<AiStatus>('/ai/status'), staleTime: 60_000 });

export const AI_UNAVAILABLE: Record<NonNullable<AiStatus['reason']>, string> = {
  not_set_up: 'AI isn’t available yet.',
  off: 'AI is turned off for your company (Settings → AI assistant).',
  no_credits: 'AI credits are used up. An admin can add more in Settings → Plan & credits.',
};

export type Heat = 'hot' | 'warm' | 'cold';
export const HEAT: Record<Heat, { label: string; emoji: string; tone: 'red' | 'amber' | 'gray' }> = {
  hot: { label: 'Hot', emoji: '🔥', tone: 'red' },
  warm: { label: 'Warm', emoji: '🌤️', tone: 'amber' },
  cold: { label: 'Cold', emoji: '❄️', tone: 'gray' },
};

export interface Diagnosis { summary: string; temperature: Heat; risks: string[]; nextSteps: string[]; suggestedMessage: string }
export interface QuickNoteResult { note: string; followupAt: string | null; followupNote: string | null; statusId: string | null; statusName: string | null }
export interface ChatMessage { role: 'user' | 'assistant'; text: string }
export interface ChatSummary { id: string; title: string; updatedAt: string }

export interface CallInsight {
  id: string; aiStatus: string | null; aiError: string | null; transcript: string | null; aiSummary: string | null;
  aiSentiment: 'positive' | 'neutral' | 'negative' | null; aiRating: number | null;
  aiAudit: { check: string; passed: boolean; note: string }[] | null; aiNextStep: string | null; aiAt: string | null;
}
export const SENTIMENT = {
  positive: { label: 'Positive', emoji: '🙂', tone: 'green' },
  neutral: { label: 'Neutral', emoji: '😐', tone: 'gray' },
  negative: { label: 'Negative', emoji: '🙁', tone: 'red' },
} as const;

/** ₹ from paise, e.g. 12345 → "₹123.45" (whole rupees shown without decimals). */
export const paise = (p: number) => {
  const r = p / 100;
  return `${r < 0 ? '−' : ''}₹${Math.abs(r).toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 })}`;
};
